import React from 'react';
import { Text, Box } from '@nimbus-ds/components';
import type { OrdersListProductDto } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { ErrorScreen, ImageItemStratus } from 'commons/components';
import { useTranslationCustomers } from 'domains/Customers/hooks';
import { CustomerSubscriptionsProductsCardSkeleton } from './CustomerSubscriptionsProductsCardSkeleton';

interface CustomerSubscriptionsProductsProps {
  show: boolean;
  products?: OrdersListProductDto[];
  isLoading: boolean;
  isError: boolean;
  fetchProducts: () => void;
}

const CustomerSubscriptionsProductsCard = React.memo(
  function CustomerSubscriptionsProductsCard({
    show,
    products,
    isLoading,
    isError,
    fetchProducts,
  }: Readonly<CustomerSubscriptionsProductsProps>): JSX.Element | null {
    const t = useTranslationCustomers('customerDetails.customerSubscriptions');
    const { goTo } = useNavegate();

    const goToProduct = (productId: string) => {
      goTo(`/products/${productId}`);
    };

    if (!show) return null;

    if (isLoading) return <CustomerSubscriptionsProductsCardSkeleton />;

    if (isError)
      return (
        <ErrorScreen description={t('errorProducts')} onRetry={fetchProducts} />
      );

    return (
      <div>
        {products?.map((product) => (
          <Box
            display="flex"
            backgroundColor="neutral-surface"
            padding="1"
            marginBottom="1"
            gap="2"
            key={`product-${product.productId}`}
          >
            <ImageItemStratus
              id={product.productId}
              title={product.title}
              trimTitle
              imageUrl={product.photoUrl}
              imageWidth="70px"
              onClickTitle={() => goToProduct(product.productId)}
              onClickImage={() => goToProduct(product.productId)}
            >
              {!!product.variant && (
                <Text fontSize="caption">{product.variant}</Text>
              )}
              {!!product.sku && (
                <Text fontSize="caption">
                  {t('resultProducts.sku', { sku: product.sku })}
                </Text>
              )}
              <Text fontSize="caption">{product.quantity}x</Text>
            </ImageItemStratus>
          </Box>
        ))}
      </div>
    );
  },
);

export default CustomerSubscriptionsProductsCard;
